import {
	Calendar,
	type LucideIcon,
	Mail,
	MapPin,
	Navigation,
	Phone,
	PhoneCall,
	Send,
	User,
} from "lucide-react";
import { useMemo } from "react";
import { useClientQuery } from "web_myplann/Api/Client/useClientQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { formatProfileAddress } from "web_myplann/Profile/Utils/formatProfileAddress";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import {
	handleEmailClick,
	handlePhoneClick,
} from "web_myplann/Shared/Utils/openExternalApps";
import { getMapsLink } from "web_myplann/Utils/getMapsLink";

const R = Resources.Profile;

type ProfileAccountItem = {
	id: string;
	icon: LucideIcon;
	label: string;
	value: string;
	actionIcon?: LucideIcon;
	onClick?: () => void;
};

export const useProfileOverview = () => {
	const { t } = useResource();
	const { data: userData, isLoading } = useClientQuery();

	const clientNotFound = !isLoading && !userData?.data;
	const data = userData?.data;

	const name = `${data?.firstName ?? ""} ${data?.lastName ?? ""}`.trim();
	const email = userData?.contactEmail ?? "";
	const phone = userData?.contactPhone ?? "";
	const address = formatProfileAddress(userData?.addresses?.[0]);
	const photo = userData?.photo ?? undefined;

	const birthDate = data?.bornDate ? dateFormatter(data.bornDate) : "";

	const accountItems = useMemo<ProfileAccountItem[]>(
		() =>
			[
				{ id: "name", icon: User, label: t(R.Overview.name), value: name },
				{
					id: "email",
					icon: Mail,
					label: t(R.Fields.email),
					value: email,
					actionIcon: Send,
					onClick: () => handleEmailClick(email),
				},
				{
					id: "phone",
					icon: Phone,
					label: t(R.Fields.phone),
					value: phone,
					actionIcon: PhoneCall,
					onClick: () => handlePhoneClick(phone),
				},
				{
					id: "address",
					icon: MapPin,
					label: t(R.Overview.address),
					value: address,
					actionIcon: Navigation,
					onClick: () => window.open(getMapsLink(address), "_blank"),
				},
				{
					id: "birthDate",
					icon: Calendar,
					label: t(R.Overview.birthDate),
					value: birthDate,
				},
			].filter((item) => item.value),
		[t, name, email, phone, address, birthDate],
	);

	return { isLoading, clientNotFound, name, email, photo, accountItems };
};
